import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ClipboardList, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import {
  OUTREACH_OUTCOME_LABEL,
  ACQUISITION_STAGE_BADGE,
  ACQUISITION_STAGE_LABEL,
  ACTIVE_CLAIM_STATUSES,
  CLAIM_STATUS_BADGE,
  CLAIM_STATUS_LABEL,
  LOSS_REASON_LABEL,
  LOSS_REASONS,
  SALES_GOAL_BADGE,
  SALES_GOAL_LABEL,
  SALES_GOALS,
  formatGhs,
  isInPool,
  type ClaimStatus,
  type LossReason,
  type OutreachFeedbackOutcome,
  type OutreachFeedbackSentiment,
  type SalesClaim,
  type SalesGoal,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { QuotaBar, Segmented, StatusBadge, btnGhost, btnPrimary, compactGhs, selectCls, timeLeftLabel, toastSales } from './components/sales-ui';
import {
  useAdvanceClaimMutation,
  useCommitClaimMutation,
  useLoseClaimMutation,
  useOutreachFeedbackMutation,
  usePursueClaimMutation,
  useSalesClaimsQuery,
  useSalesDashboardQuery,
  useSalesOutreachQuery,
  useSalesPurposesQuery,
  useSalesRepsQuery,
  useSalesSubjectsQuery,
  useWinClaimMutation,
} from '@/client/api/sales/sales';
import { DEFAULT_SALES_POLICY } from '@/client/types/sales';
import {
  achievedGoalsFor,
  availabilityFor,
  displayClaimStatus,
} from './sales-live';
import { NO_ONE, promptActAs, useSalesActing } from './sales-store';

const STAGES: { id: ClaimStatus; label: string }[] = [
  { id: 'claimed', label: 'Claimed' },
  { id: 'in_pursuit', label: 'In pursuit' },
  { id: 'committed', label: 'Committed' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

type PipelineLayout = 'board' | 'list' | 'table';
const LAYOUT_KEY = 'ff-admin.sales.pipeline.layout';
const ORDER_KEY = 'ff-admin.sales.pipeline.order.v1';
type RankMap = Record<string, number>;
type OrderStore = Record<string, RankMap>;

function loadLayout(): PipelineLayout {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'board' || v === 'list' || v === 'table') return v;
  } catch {
    /* private mode */
  }
  return 'board';
}

function loadOrder(): OrderStore {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as OrderStore;
  } catch {
    /* private mode */
  }
  return {};
}

function sortByRank(rows: SalesClaim[], rank: RankMap): SalesClaim[] {
  return [...rows].sort((a, b) => (rank[a.id] ?? 1e9) - (rank[b.id] ?? 1e9));
}

function claimValue(c: SalesClaim, purposes: { id: string; estimatedValueGhs: number }[]): number {
  return purposes.find((p) => p.id === c.purposeId)?.estimatedValueGhs ?? 0;
}

function allowedTargets(from: ClaimStatus): ClaimStatus[] {
  if (from === 'claimed') return ['in_pursuit', 'committed', 'won', 'lost'];
  if (from === 'in_pursuit') return ['committed', 'won', 'lost'];
  if (from === 'committed') return ['won', 'lost'];
  return [];
}

function colId(stage: ClaimStatus) {
  return `col_${stage}`;
}

function DroppableColumn({
  stage,
  children,
}: {
  stage: ClaimStatus;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: colId(stage) });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex w-[16.5rem] shrink-0 flex-col rounded-xl border bg-gray-50/80',
        isOver ? 'border-slate-400 bg-slate-50' : 'border-gray-200',
      )}
    >
      {children}
    </section>
  );
}

function ClaimFace({
  claim,
  showOwner,
  now,
  footer,
  showGrip,
  subject,
  purpose,
  owner,
}: {
  claim: SalesClaim;
  showOwner: boolean;
  now: number;
  footer?: ReactNode;
  showGrip?: boolean;
  subject?: { displayName: string; acquisitionStage: string };
  purpose?: { name: string; goal: SalesGoal; estimatedValueGhs: number };
  owner?: { name: string };
}) {
  const status = displayClaimStatus(claim, now);
  const active = ACTIVE_CLAIM_STATUSES.includes(claim.status) && new Date(claim.expiresAt).getTime() > now;
  return (
    <>
      {showGrip ? (
        <span className="flex shrink-0 items-start px-1.5 pt-3 text-gray-400" aria-hidden>
          <GripVertical className="h-4 w-4" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <span className="line-clamp-2 text-sm font-medium text-gray-900">{subject?.displayName ?? claim.subjectId}</span>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-800">
              {compactGhs(purpose?.estimatedValueGhs ?? 0)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {purpose && <StatusBadge label={SALES_GOAL_LABEL[purpose.goal]} className={SALES_GOAL_BADGE[purpose.goal]} />}
            {subject && (
              <StatusBadge
                label={ACQUISITION_STAGE_LABEL[subject.acquisitionStage as keyof typeof ACQUISITION_STAGE_LABEL]}
                className={ACQUISITION_STAGE_BADGE[subject.acquisitionStage as keyof typeof ACQUISITION_STAGE_BADGE]}
              />
            )}
            {showOwner && owner && <span className="text-[11px] text-gray-500">{owner.name.split(' ')[0]}</span>}
          </div>
          {active && (
            <p className={`mt-2 text-[11px] font-medium ${status === 'expired' ? 'text-rose-700' : 'text-gray-400'}`}>
              {timeLeftLabel(claim.expiresAt, now)}
            </p>
          )}
          {claim.status === 'lost' && claim.lossReason && (
            <p className="mt-2 text-[11px] text-gray-400">{LOSS_REASON_LABEL[claim.lossReason]}</p>
          )}
        </div>
        {footer ? (
          <div data-claim-action onPointerDown={(e) => e.stopPropagation()}>
            {footer}
          </div>
        ) : null}
      </div>
    </>
  );
}

function ClaimCard({
  claim,
  canDrag,
  showOwner,
  now,
  onOpen,
  footer,
  subject,
  purpose,
  owner,
}: {
  claim: SalesClaim;
  canDrag: boolean;
  showOwner: boolean;
  now: number;
  onOpen: () => void;
  footer?: ReactNode;
  subject?: { displayName: string; acquisitionStage: string };
  purpose?: { name: string; goal: SalesGoal; estimatedValueGhs: number };
  owner?: { name: string };
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: claim.id,
    disabled: !canDrag,
  });
  return (
    <div
      ref={setNodeRef}
      style={isDragging ? undefined : { transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex rounded-lg border border-gray-200 bg-white shadow-sm',
        canDrag && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-claim-action]')) return;
        onOpen();
      }}
    >
      <ClaimFace
        claim={claim}
        showOwner={showOwner}
        now={now}
        footer={footer}
        showGrip={canDrag}
        subject={subject}
        purpose={purpose}
        owner={owner}
      />
    </div>
  );
}

export default function SalesPipelinePage() {
  const navigate = useNavigate();
  const { rep } = useSalesActing();
  const claimsQ = useSalesClaimsQuery({ limit: 200 });
  const subjectsQ = useSalesSubjectsQuery({ limit: 200 });
  const purposesQ = useSalesPurposesQuery();
  const repsQ = useSalesRepsQuery();
  const outreachQ = useSalesOutreachQuery({ limit: 200 });
  const dashQ = useSalesDashboardQuery({ period: '30d', compare: 'prior', scope: rep ? 'me' : 'floor' });
  const pursueMut = usePursueClaimMutation();
  const commitMut = useCommitClaimMutation();
  const winMut = useWinClaimMutation();
  const loseMut = useLoseClaimMutation();
  const advanceMut = useAdvanceClaimMutation();
  const feedbackMut = useOutreachFeedbackMutation();
  const claims = claimsQ.data?.claims ?? [];
  const subjects = subjectsQ.data?.subjects ?? [];
  const purposes = purposesQ.data ?? [];
  const reps = repsQ.data ?? [];
  const outreaches = outreachQ.data?.outreach ?? [];
  const purposeById = (id: string) => purposes.find((p) => p.id === id);
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  const getRep = (id: string) => reps.find((r) => r.id === id);
  const [goal, setGoal] = useState<SalesGoal | 'all'>('all');
  const [layout, setLayout] = useState<PipelineLayout>(loadLayout);
  const [ranks, setRanks] = useState<OrderStore>(loadOrder);
  const viewerKey = rep?.id ?? NO_ONE;
  const rank = ranks[viewerKey] ?? {};
  const [pending, setPending] = useState<{ claim: SalesClaim; kind: 'won' | 'lost' | 'advance' } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [winEvidence, setWinEvidence] = useState('');
  const [lossReason, setLossReason] = useState<LossReason>('not_a_fit');
  const [advancePurpose, setAdvancePurpose] = useState('');
  const [fbOutcome, setFbOutcome] = useState<OutreachFeedbackOutcome>('interested');
  const [fbSentiment, setFbSentiment] = useState<OutreachFeedbackSentiment>('neutral');
  const now = Date.now();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, layout);
    } catch {
      /* private mode */
    }
  }, [layout]);

  useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(ranks));
    } catch {
      /* private mode */
    }
  }, [ranks]);

  const scoped = useMemo(() => {
    return claims.filter((c) => {
      if (rep && c.ownerId !== rep.id) return false;
      const p = purposeById(c.purposeId);
      if (goal !== 'all' && p?.goal !== goal) return false;
      return true;
    });
  }, [claims, rep, goal, purposes]);

  const columns = useMemo(() => {
    return STAGES.map((stage) => {
      const rows = scoped.filter((c) => {
        if (stage.id === 'claimed' || stage.id === 'in_pursuit' || stage.id === 'committed') {
          return ACTIVE_CLAIM_STATUSES.includes(c.status) && displayClaimStatus(c, now) === stage.id;
        }
        return c.status === stage.id;
      });
      const ghs = rows.reduce((s, c) => s + claimValue(c, purposes), 0);
      return { ...stage, rows: sortByRank(rows, rank), ghs };
    });
  }, [scoped, now, rank, purposes]);

  const cooling = useMemo(
    () => subjects.filter((s) => isInPool(s) && availabilityFor(s, claims) === 'cooling'),
    [subjects, claims],
  );

  const inPlay = columns.slice(0, 3).reduce((s, col) => s + col.ghs, 0);
  const queue = useMemo(() => {
    if (!rep) return [];
    return scoped
      .filter((c) => ACTIVE_CLAIM_STATUSES.includes(c.status))
      .sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime());
  }, [rep, scoped]);
  const next = queue[0];
  const nextSubject = next ? subjectById(next.subjectId) : undefined;
  const nextPurpose = next ? purposeById(next.purposeId) : undefined;
  const perf = dashQ.data?.current;

  const canMove = (c: SalesClaim) => {
    if (!rep) return false;
    if (!ACTIVE_CLAIM_STATUSES.includes(c.status) || new Date(c.expiresAt).getTime() <= now) return false;
    return c.ownerId === rep.id;
  };

  const applyStage = (claim: SalesClaim, nextStatus: ClaimStatus): boolean => {
    if (displayClaimStatus(claim, now) === nextStatus) return false;
    if (!canMove(claim)) {
      if (rep) toast.error('Only you can move a claim you own.');
      else promptActAs('move a claim');
      return false;
    }
    if (!allowedTargets(displayClaimStatus(claim, now)).includes(nextStatus)) {
      toast.error(
        `Can't move from ${CLAIM_STATUS_LABEL[displayClaimStatus(claim, now)]} to ${CLAIM_STATUS_LABEL[nextStatus]}.`,
      );
      return false;
    }
    if (nextStatus === 'won' || nextStatus === 'lost') {
      const sub = subjectById(claim.subjectId);
      setWinEvidence(sub?.platform?.uid != null ? String(sub.platform.uid) : '');
      setLossReason('not_a_fit');
      setPending({ claim, kind: nextStatus });
      return false;
    }
    const onOk = () => toast.success(`Moved to ${CLAIM_STATUS_LABEL[nextStatus]}`);
    if (nextStatus === 'in_pursuit') pursueMut.mutate({ id: claim.id }, { onSuccess: onOk, onError: toastSales });
    else commitMut.mutate({ id: claim.id }, { onSuccess: onOk, onError: toastSales });
    return true;
  };

  const putColumnOrder = (orderedIds: string[]) => {
    setRanks((prev) => {
      const next = { ...(prev[viewerKey] ?? {}) };
      orderedIds.forEach((id, i) => {
        next[id] = i;
      });
      return { ...prev, [viewerKey]: next };
    });
  };

  const pendingOutreachFeedback = (subjectId: string) =>
    outreaches
      .filter((o) => o.subjectId === subjectId && o.direction === 'outbound' && !o.feedback && o.status !== 'draft' && o.status !== 'blocked')
      .sort((a, b) => b.at.localeCompare(a.at))[0];

  const submitPending = () => {
    if (!pending) return;
    if (!rep) {
      promptActAs('move a claim');
      return;
    }
    const purpose = purposeById(pending.claim.purposeId);
    if (pending.kind === 'advance') {
      if (!advancePurpose) return;
      advanceMut.mutate(
        { id: pending.claim.id, body: { purposeId: advancePurpose } },
        {
          onSuccess: () => {
            toast.success('Advanced — new claim opened');
            setPending(null);
          },
          onError: toastSales,
        },
      );
      return;
    }
    if (pending.kind === 'lost') {
      const awaiting = pendingOutreachFeedback(pending.claim.subjectId);
      const finish = () =>
        loseMut.mutate(
          { id: pending.claim.id, reason: lossReason },
          {
            onSuccess: () => {
              toast.success('Marked lost');
              setPending(null);
            },
            onError: toastSales,
          },
        );
      if (awaiting) {
        feedbackMut.mutate(
          { id: awaiting.id, sentiment: fbSentiment, outcome: fbOutcome },
          { onSuccess: finish, onError: toastSales },
        );
      } else finish();
      return;
    }
    winMut.mutate(
      {
        id: pending.claim.id,
        evidence: {
          uid: purpose?.goal === 'signup' ? Number(winEvidence) : undefined,
          listingId: purpose?.goal === 'supply' ? winEvidence : undefined,
          promotionId: purpose?.goal === 'monetize' ? winEvidence : undefined,
        },
      },
      {
        onSuccess: (won) => {
          toast.success('Marked won');
          const leftover = purposes.filter((p) => p.active && !achievedGoalsFor(won.subjectId, claims).includes(p.goal));
          if (leftover.length) {
            setAdvancePurpose(leftover[0].id);
            setPending({ claim: won, kind: 'advance' });
          } else {
            setPending(null);
          }
        },
        onError: toastSales,
      },
    );
  };

  const onDragStart = (event: DragStartEvent) => {
    setDraggingId(String(event.active.id));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setDraggingId(null);
    const overId = event.over?.id;
    if (overId == null) return;
    const over = String(overId);
    const claim = claims.find((c) => c.id === event.active.id);
    if (!claim) return;
    const droppedOn = claims.find((c) => c.id === over);
    const from = displayClaimStatus(claim);
    const to = over.startsWith('col_')
      ? (over.slice(4) as ClaimStatus)
      : droppedOn
        ? displayClaimStatus(droppedOn)
        : undefined;
    if (!to) return;
    if (from !== to && !applyStage(claim, to)) return;

    const dest = columns.find((col) => col.id === to);
    if (!dest) return;
    const destIds = dest.rows.map((c) => c.id).filter((id) => id !== claim.id);
    if (from === to) {
      const ids = dest.rows.map((c) => c.id);
      const oldIndex = ids.indexOf(claim.id);
      const newIndex = over.startsWith('col_') ? ids.length - 1 : ids.indexOf(over);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      putColumnOrder(arrayMove(ids, oldIndex, newIndex));
      return;
    }
    let insertAt = destIds.length;
    if (droppedOn && displayClaimStatus(droppedOn) === to) {
      const at = destIds.indexOf(droppedOn.id);
      if (at >= 0) insertAt = at;
    }
    destIds.splice(insertAt, 0, claim.id);
    putColumnOrder(destIds);
  };

  const draggingClaim = draggingId ? claims.find((c) => c.id === draggingId) : undefined;

  const pendingPurpose = pending ? purposeById(pending.claim.purposeId) : undefined;
  const winPlaceholder =
    pendingPurpose?.goal === 'supply' ? 'listing id' : pendingPurpose?.goal === 'monetize' ? 'promotion id' : 'uid';
  const pendingFeedback = pending?.kind === 'lost' ? pendingOutreachFeedback(pending.claim.subjectId) : undefined;
  const nextPurposes = pending
    ? purposes.filter((p) => p.active && !achievedGoalsFor(pending.claim.subjectId, claims).includes(p.goal))
    : [];
  const showBook = Boolean(rep);

  return (
    <SalesPageShell
      title="Pipeline"
      description={
        rep
          ? `The book for ${rep.name}. Drag to sort this view, or drop on another column to change stage.`
          : 'Everyone’s book. Act as someone to move a claim. Drag still sorts this view for you.'
      }
      crumbs={[{ label: 'Pipeline' }]}
      icon={ClipboardList}
      actions={
        <select className={selectCls} value={goal} onChange={(e) => setGoal(e.target.value as SalesGoal | 'all')}>
          <option value="all">All goals</option>
          {SALES_GOALS.map((g) => (
            <option key={g} value={g}>
              {SALES_GOAL_LABEL[g]}
            </option>
          ))}
        </select>
      }
    >
      {showBook && (
        <div className="mb-4 grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          {next && nextSubject && nextPurpose ? (
            <button
              type="button"
              onClick={() => navigate(`/admin/sales/prospects/${next.subjectId}`)}
              className="rounded-2xl border border-gray-200 bg-white p-6 text-left transition-colors hover:border-gray-300 hover:bg-gray-50/60"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Next</p>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">{nextSubject.displayName}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {nextSubject.areas.map((a) => a.region).join(', ') || 'No area'} · {nextPurpose.name}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge label={SALES_GOAL_LABEL[nextPurpose.goal]} className={SALES_GOAL_BADGE[nextPurpose.goal]} />
                <StatusBadge
                  label={CLAIM_STATUS_LABEL[displayClaimStatus(next)]}
                  className={CLAIM_STATUS_BADGE[displayClaimStatus(next)]}
                />
                <StatusBadge
                  label={ACQUISITION_STAGE_LABEL[nextSubject.acquisitionStage]}
                  className={ACQUISITION_STAGE_BADGE[nextSubject.acquisitionStage]}
                />
                <span className={`text-xs font-medium ${new Date(next.expiresAt).getTime() <= now ? 'text-rose-700' : 'text-gray-500'}`}>
                  {timeLeftLabel(next.expiresAt, now)}
                </span>
              </div>
            </button>
          ) : (
            <div className="flex flex-col justify-center rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10">
              <p className="text-base font-semibold text-gray-900">Nothing in your book</p>
              <p className="mt-1 max-w-sm text-sm text-gray-500">Pull from the shared pool or find people already listing on other sites.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/admin/sales/prospects" className={btnPrimary}>
                  Prospects
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/admin/sales/prospects/search" className={btnGhost}>
                  Search
                </Link>
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <QuotaBar won={perf?.wonGhs ?? 0} quota={perf?.quotaGhs ?? 0} />
            <p className="mt-4 text-sm text-gray-500">
              {queue.length} / {DEFAULT_SALES_POLICY.maxConcurrentPerRep} capacity · {formatGhs(perf?.pipelineGhs ?? inPlay)} in
              play
            </p>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-500">
            In play <span className="font-semibold tabular-nums text-gray-900">{formatGhs(inPlay)}</span>
          </span>
          <span className="text-gray-500">
            Won <span className="font-semibold tabular-nums text-emerald-700">{formatGhs(columns[3].ghs)}</span>
          </span>
          <span className="text-gray-500">
            Lost <span className="font-semibold tabular-nums text-rose-700">{formatGhs(columns[4].ghs)}</span>
          </span>
        </div>
        <div role="group" aria-label="Pipeline layout">
          <Segmented
            value={layout}
            onChange={setLayout}
            options={[
              { id: 'board', label: 'Board' },
              { id: 'list', label: 'List' },
              { id: 'table', label: 'Table' },
            ]}
          />
        </div>
      </div>

      {layout === 'board' ? (
        <>
          <p className="mb-2 text-xs text-gray-500">
            Drag to sort in this Viewing as, or drop on another column to change stage. Won and lost ask for a reason.
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDraggingId(null)}
          >
            <div className="flex gap-3 overflow-x-auto pb-2">
              {columns.map((col) => (
                <DroppableColumn key={col.id} stage={col.id}>
                  <header className="flex items-baseline justify-between gap-2 px-3 py-2.5">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900">{col.label}</h2>
                      <p className="text-[11px] tabular-nums text-gray-400">
                        {col.rows.length} · {compactGhs(col.ghs)}
                      </p>
                    </div>
                  </header>
                  <div className="flex min-h-[18rem] flex-1 flex-col gap-2 px-2 pb-2">
                    {col.rows.length === 0 && <p className="px-1 py-6 text-center text-xs text-gray-400">Drop here</p>}
                    <SortableContext items={col.rows.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                      {col.rows.map((c) => (
                        <ClaimCard
                          key={c.id}
                          claim={c}
                          canDrag
                          showOwner={!rep}
                          now={now}
                          subject={subjectById(c.subjectId)}
                          purpose={purposeById(c.purposeId)}
                          owner={getRep(c.ownerId)}
                          onOpen={() => navigate(`/admin/sales/prospects/${c.subjectId}`)}
                          footer={
                            col.id === 'won' &&
                            rep &&
                            c.ownerId === rep.id &&
                            purposes.some((p) => p.active && !achievedGoalsFor(c.subjectId, claims).includes(p.goal)) ? (
                              <div className="border-t border-gray-100 px-3 py-2">
                                <button
                                  type="button"
                                  className="text-xs font-medium text-blue-700 hover:underline"
                                  onClick={() => {
                                    setAdvancePurpose(
                                      purposes.find((p) => p.active && !achievedGoalsFor(c.subjectId, claims).includes(p.goal))?.id ??
                                        '',
                                    );
                                    setPending({ claim: c, kind: 'advance' });
                                  }}
                                >
                                  Next goal
                                </button>
                              </div>
                            ) : null
                          }
                        />
                      ))}
                    </SortableContext>
                  </div>
                </DroppableColumn>
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {draggingClaim ? (
                <div className="flex w-[15.5rem] cursor-grabbing rounded-lg border border-gray-200 bg-white shadow-lg">
                  <ClaimFace
                    claim={draggingClaim}
                    showOwner={!rep}
                    now={now}
                    showGrip
                    subject={subjectById(draggingClaim.subjectId)}
                    purpose={purposeById(draggingClaim.purposeId)}
                    owner={getRep(draggingClaim.ownerId)}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </>
      ) : layout === 'list' ? (
        <div className="space-y-4">
          {columns.map((col) => (
            <section key={col.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <header className="flex items-baseline justify-between border-b border-gray-100 px-4 py-2.5">
                <h2 className="text-sm font-semibold text-gray-900">{col.label}</h2>
                <p className="text-xs tabular-nums text-gray-500">
                  {col.rows.length} · {compactGhs(col.ghs)}
                </p>
              </header>
              {col.rows.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-400">Empty</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {col.rows.map((c) => {
                    const sub = subjectById(c.subjectId);
                    const purpose = purposeById(c.purposeId);
                    const owner = getRep(c.ownerId);
                    const status = displayClaimStatus(c);
                    const movable = canMove(c);
                    return (
                      <li key={c.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <Link
                            to={`/admin/sales/prospects/${c.subjectId}`}
                            className="font-medium text-gray-900 hover:text-slate-700"
                          >
                            {sub?.displayName ?? c.subjectId}
                          </Link>
                          <p className="mt-0.5 text-xs text-gray-600">
                            {purpose ? SALES_GOAL_LABEL[purpose.goal] : '—'}
                            {sub ? ` · ${ACQUISITION_STAGE_LABEL[sub.acquisitionStage]}` : ''}
                            {!rep && owner ? ` · ${owner.name}` : ''}
                            {movable ? ` · ${timeLeftLabel(c.expiresAt, now)}` : ''}
                            {c.lossReason ? ` · ${LOSS_REASON_LABEL[c.lossReason]}` : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="text-sm tabular-nums text-gray-900">{compactGhs(claimValue(c, purposes))}</span>
                          {movable ? (
                            <select
                              aria-label={`Move ${sub?.displayName ?? 'claim'}`}
                              className={selectCls}
                              value={status}
                              onChange={(e) => applyStage(c, e.target.value as ClaimStatus)}
                            >
                              <option value={status}>{CLAIM_STATUS_LABEL[status]}</option>
                              {allowedTargets(status).map((id) => (
                                <option key={id} value={id}>
                                  {CLAIM_STATUS_LABEL[id]}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <StatusBadge label={CLAIM_STATUS_LABEL[status]} className={CLAIM_STATUS_BADGE[status]} />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <caption className="sr-only">Pipeline claims</caption>
            <thead className="border-b border-gray-200 text-left text-xs font-medium text-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Name
                </th>
                <th scope="col" className="px-4 py-3">
                  Stage
                </th>
                <th scope="col" className="px-4 py-3">
                  Goal
                </th>
                {!rep && (
                  <th scope="col" className="px-4 py-3">
                    Owner
                  </th>
                )}
                <th scope="col" className="px-4 py-3">
                  Exclusive
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {scoped.length === 0 ? (
                <tr>
                  <td colSpan={!rep ? 6 : 5} className="px-4 py-10 text-center text-sm text-gray-500">
                    Nothing in this slice.
                  </td>
                </tr>
              ) : (
                scoped.map((c) => {
                  const sub = subjectById(c.subjectId);
                  const purpose = purposeById(c.purposeId);
                  const owner = getRep(c.ownerId);
                  const status = displayClaimStatus(c);
                  const movable = canMove(c);
                  return (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/sales/prospects/${c.subjectId}`}
                          className="font-medium text-gray-900 hover:text-slate-700"
                        >
                          {sub?.displayName ?? c.subjectId}
                        </Link>
                        {sub && (
                          <div className="text-xs text-gray-600">{ACQUISITION_STAGE_LABEL[sub.acquisitionStage]}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {movable ? (
                          <select
                            aria-label={`Move ${sub?.displayName ?? 'claim'}`}
                            className={selectCls}
                            value={status}
                            onChange={(e) => applyStage(c, e.target.value as ClaimStatus)}
                          >
                            <option value={status}>{CLAIM_STATUS_LABEL[status]}</option>
                            {allowedTargets(status).map((id) => (
                              <option key={id} value={id}>
                                {CLAIM_STATUS_LABEL[id]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <StatusBadge label={CLAIM_STATUS_LABEL[status]} className={CLAIM_STATUS_BADGE[status]} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{purpose ? SALES_GOAL_LABEL[purpose.goal] : '—'}</td>
                      {!rep && <td className="px-4 py-3 text-gray-700">{owner?.name ?? c.ownerId}</td>}
                      <td className="px-4 py-3 text-gray-600">
                        {movable ? timeLeftLabel(c.expiresAt, now) : c.lossReason ? LOSS_REASON_LABEL[c.lossReason] : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-gray-900">
                        {compactGhs(claimValue(c, purposes))}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {cooling.length > 0 && (
        <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <h2 className="mb-2 text-sm font-semibold text-amber-950">Cooling</h2>
          <p className="mb-3 text-xs text-amber-800">
            Subject-wide lock after lose, release, or expiry. Nobody can claim until thaw.
          </p>
          <ul className="flex flex-wrap gap-2">
            {cooling.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/sales/prospects/${s.id}`)}
                  className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs text-amber-950 hover:bg-amber-50"
                >
                  {s.displayName}
                  {s.subjectCooldownUntil ? ` · ${new Date(s.subjectCooldownUntil).toLocaleDateString()}` : ''}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">
              {pending.kind === 'won' ? 'Mark won' : pending.kind === 'lost' ? 'Mark lost' : 'Next goal'} ·{' '}
              {subjectById(pending.claim.subjectId)?.displayName ?? pending.claim.subjectId}
            </h3>
            {pending.kind === 'won' ? (
              <>
                <p className="mb-3 mt-1 text-sm text-gray-500">
                  {pendingPurpose?.goal === 'signup'
                    ? 'Signup needs a FeelyFeely uid.'
                    : pendingPurpose?.goal === 'supply'
                      ? 'Supply needs a listing id.'
                      : 'Monetize needs a promotion id.'}
                </p>
                <label className="mb-2 block text-xs text-gray-500">{winPlaceholder}</label>
                <input
                  className={`${selectCls} mb-4 w-full`}
                  value={winEvidence}
                  onChange={(e) => setWinEvidence(e.target.value)}
                  placeholder={winPlaceholder}
                />
              </>
            ) : pending.kind === 'advance' ? (
              <>
                <p className="mb-3 mt-1 text-sm text-gray-500">Open the next unused purpose on this prospect.</p>
                <label className="mb-2 block text-xs text-gray-500">Purpose</label>
                <select
                  className={`${selectCls} mb-4 w-full`}
                  value={advancePurpose}
                  onChange={(e) => setAdvancePurpose(e.target.value)}
                >
                  {nextPurposes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <p className="mb-3 mt-1 text-sm text-gray-500">Pick why this claim closed without a win.</p>
                {pendingFeedback ? (
                  <div className="mb-3 grid gap-2 sm:grid-cols-2">
                    <label className="block text-xs text-gray-500">
                      Last outreach
                      <select
                        className={`${selectCls} mt-1 w-full`}
                        value={fbSentiment}
                        onChange={(e) => setFbSentiment(e.target.value as OutreachFeedbackSentiment)}
                      >
                        <option value="positive">Positive</option>
                        <option value="neutral">Neutral</option>
                        <option value="negative">Negative</option>
                      </select>
                    </label>
                    <label className="block text-xs text-gray-500">
                      Outcome
                      <select
                        className={`${selectCls} mt-1 w-full`}
                        value={fbOutcome}
                        onChange={(e) => setFbOutcome(e.target.value as OutreachFeedbackOutcome)}
                      >
                        {(Object.keys(OUTREACH_OUTCOME_LABEL) as OutreachFeedbackOutcome[]).map((k) => (
                          <option key={k} value={k}>
                            {OUTREACH_OUTCOME_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}
                <label className="mb-2 block text-xs text-gray-500">Reason</label>
                <select
                  className={`${selectCls} mb-4 w-full`}
                  value={lossReason}
                  onChange={(e) => setLossReason(e.target.value as LossReason)}
                >
                  {LOSS_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {LOSS_REASON_LABEL[r]}
                    </option>
                  ))}
                </select>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setPending(null)}>
                {pending.kind === 'advance' ? 'Skip' : 'Cancel'}
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={submitPending}
                disabled={
                  (pending.kind === 'won' && !winEvidence.trim()) || (pending.kind === 'advance' && !advancePurpose)
                }
              >
                {pending.kind === 'won' ? 'Mark won' : pending.kind === 'lost' ? 'Mark lost' : 'Advance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SalesPageShell>
  );
}
